import { useEffect, useRef, useState } from 'react';
import { Camera, Check, HeartPulse, RefreshCw, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { analyzePpg, channelMeans, fingerDetected, type PpgResult, type PpgSample } from '@/lib/ppg';

const SCAN_MS = 12_000;
const FRAME_W = 64;
const FRAME_H = 48;

type Phase = 'idle' | 'scanning' | 'done' | 'error';

interface PulseScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the measured BPM when the user accepts the reading. */
  onResult: (bpm: number) => void;
}

/**
 * Camera PPG pulse scanner: measures heart rate through the fingertip using
 * the phone's back camera (and torch, when the browser allows it).
 */
export function PulseScannerDialog({ open, onOpenChange, onResult }: PulseScannerDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameCanvasRef = useRef<HTMLCanvasElement>(null);
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef(0);
  const samplesRef = useRef<PpgSample[]>([]);
  const startRef = useRef(0);

  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [liveBpm, setLiveBpm] = useState<number | null>(null);
  const [hasFinger, setHasFinger] = useState(false);
  const [result, setResult] = useState<PpgResult | null>(null);

  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const reset = () => {
    stopCamera();
    samplesRef.current = [];
    setPhase('idle');
    setResult(null);
    setLiveBpm(null);
    setProgress(0);
    setHasFinger(false);
    setErrorMessage(null);
  };

  // Reset whenever the dialog closes; stop the camera on unmount.
  useEffect(() => {
    if (!open) reset();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const drawWaveform = () => {
    const canvas = waveCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const values = samplesRef.current.slice(-180).map((s) => s.v);
    if (values.length < 2) return;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1e-6, max - min);
    const { width, height } = canvas;

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    values.forEach((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 10) - 5;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#d64f8f';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const finishScan = () => {
    stopCamera();
    const analysis = analyzePpg(samplesRef.current);
    if (analysis) {
      setResult(analysis);
      setPhase('done');
    } else {
      setErrorMessage(
        "Couldn't read a clear pulse. Cover the lens completely with your fingertip, keep still, and try again — brighter light helps.",
      );
      setPhase('error');
    }
  };

  const tick = () => {
    const video = videoRef.current;
    const canvas = frameCanvasRef.current;

    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, FRAME_W, FRAME_H);
    const frame = ctx.getImageData(0, 0, FRAME_W, FRAME_H).data;
    const { red, green } = channelMeans(frame);
    setHasFinger(fingerDetected(red));

    samplesRef.current.push({ t: performance.now(), v: green });
    const elapsed = performance.now() - startRef.current;
    setProgress(Math.min(1, elapsed / SCAN_MS));

    // Rolling estimate once we have ~5s of signal, refreshed every second.
    const samples = samplesRef.current;
    if (samples.length >= 150 && samples.length % 30 === 0) {
      const estimate = analyzePpg(samples.slice(-240));
      if (estimate) setLiveBpm(estimate.bpm);
    }

    drawWaveform();

    if (elapsed >= SCAN_MS) finishScan();
    else rafRef.current = requestAnimationFrame(tick);
  };

  const startScan = async () => {
    samplesRef.current = [];
    setResult(null);
    setLiveBpm(null);
    setProgress(0);
    setHasFinger(false);
    setErrorMessage(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMessage('Camera is not available in this browser — it needs a secure (HTTPS) page.');
      setPhase('error');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        },
        audio: false,
      });
      streamRef.current = stream;

      // Best-effort torch — unsupported on many browsers (notably iOS Safari).
      const track = stream.getVideoTracks()[0];
      try {
        await track.applyConstraints({ advanced: [{ torch: true }] } as unknown as MediaTrackConstraints);
      } catch {
        // Torch unavailable — the scan still works in decent ambient light.
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }

      startRef.current = performance.now();
      setPhase('scanning');
      rafRef.current = requestAnimationFrame(tick);
    } catch (error) {
      setErrorMessage(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Camera permission was denied. Allow camera access, then try again.'
          : 'Could not start the camera. Close other apps using it and try again.',
      );
      setPhase('error');
    }
  };

  const acceptReading = () => {
    if (result) {
      onResult(result.bpm);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Camera pulse scan</DialogTitle>
          <DialogDescription className="text-base">
            {phase === 'scanning'
              ? 'Measuring your pulse through your fingertip…'
              : 'Measure your real heart rate using your phone camera — no wearable needed.'}
          </DialogDescription>
        </DialogHeader>

        {/* Hidden video + frame-sampling canvas */}
        <video ref={videoRef} playsInline muted className="hidden" />
        <canvas ref={frameCanvasRef} width={FRAME_W} height={FRAME_H} className="hidden" />

        {phase === 'idle' && (
          <div className="space-y-5 pt-2">
            <div className="flex justify-center">
              <span className="flex size-20 items-center justify-center rounded-3xl bg-blush text-blush-foreground">
                <Camera className="size-10" aria-hidden />
              </span>
            </div>
            <ol className="space-y-2.5 text-[15px] leading-relaxed text-foreground/85">
              <li className="flex gap-2.5">
                <strong>1.</strong> Cover the back camera lens completely with your fingertip.
              </li>
              <li className="flex gap-2.5">
                <strong>2.</strong> Keep still and breathe normally for 12 seconds.
              </li>
              <li className="flex gap-2.5">
                <strong>3.</strong> If your flash turns on, gently rest your finger over it too.
              </li>
            </ol>
            <Button onClick={startScan} className="h-14 w-full rounded-2xl text-lg font-bold">
              <HeartPulse className="size-5" aria-hidden />
              Start 12-second scan
            </Button>
          </div>
        )}

        {phase === 'scanning' && (
          <div className="space-y-4 pt-2">
            <div className="rounded-2xl border border-blush-strong/30 bg-blush/40 p-3">
              <canvas ref={waveCanvasRef} width={280} height={88} className="w-full" />
            </div>

            <div className="flex items-center justify-between">
              <span
                className={cn(
                  'flex items-center gap-2 text-sm font-semibold',
                  hasFinger ? 'text-emerald-700' : 'text-amber-700',
                )}
              >
                <span
                  className={cn(
                    'size-2.5 rounded-full',
                    hasFinger ? 'bg-emerald-500' : 'animate-pulse bg-amber-400',
                  )}
                  aria-hidden
                />
                {hasFinger ? 'Fingertip detected' : 'Cover the lens with your fingertip'}
              </span>
              <span className="text-2xl font-bold tabular-nums">
                {liveBpm ? `${liveBpm} bpm` : '···'}
              </span>
            </div>

            <div className="h-3 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blush-strong to-lavender-strong transition-[width]"
                style={{ width: `${progress * 100}%` }}
              />
            </div>

            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-12 w-full rounded-2xl font-semibold"
            >
              Cancel scan
            </Button>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-5 pt-2 text-center">
            <div>
              <p className="text-6xl font-bold tabular-nums text-blush-foreground">
                {result.bpm}
                <span className="text-2xl"> bpm</span>
              </p>
              <p
                className={cn(
                  'mt-2 inline-block rounded-full px-3 py-1 text-sm font-bold',
                  result.quality === 'good'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700',
                )}
              >
                {result.quality === 'good' ? 'Good signal' : 'Fair signal — consider rescanning'}
              </p>
            </div>
            <div className="space-y-2">
              <Button onClick={acceptReading} className="h-14 w-full rounded-2xl text-lg font-bold">
                <Check className="size-5" aria-hidden />
                Use {result.bpm} bpm as resting HR
              </Button>
              <Button
                variant="outline"
                onClick={startScan}
                className="h-12 w-full rounded-2xl font-semibold"
              >
                <RefreshCw className="size-4" aria-hidden />
                Scan again
              </Button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-4 pt-2 text-center">
            <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <TriangleAlert className="size-7" aria-hidden />
            </span>
            <p className="text-[15px] leading-relaxed text-foreground/85">{errorMessage}</p>
            <div className="space-y-2">
              <Button onClick={startScan} className="h-12 w-full rounded-2xl font-bold">
                <RefreshCw className="size-4" aria-hidden />
                Try again
              </Button>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="h-12 w-full rounded-2xl font-semibold"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
